import type { Post } from '../payload-types'

/**
 * La striscia di provenienza sotto il titolo: come è nato questo articolo, in una riga.
 *
 * È il riassunto dei controlli: quanti dati, da quante fonti, verificati quando. Sta qui, e non nel
 * corpo, perché il corpo è l'unica parte della pagina che un modello scrive: una dichiarazione di
 * affidabilità scritta dallo stesso componente che può sbagliare non è una garanzia. Questi valori
 * arrivano da `posts.provenance`, che scrive il worker.
 *
 * La formula qui sotto è deliberatamente fattuale — dice che cosa è stato fatto e che cosa non è
 * stato verificato — perché una dichiarazione generica ("contenuto verificato") sarebbe l'ennesima
 * affermazione non falsificabile su una pagina che ne aveva già troppe.
 *
 * ECCEZIONE DA SAPERE, sull'apertura «scritto con l'intelligenza artificiale e revisionato
 * dall'autore»: la prima metà la reggono i dati (`generationType`, senza cui il componente non
 * rende nulla), la seconda no. Nessun campo registra la revisione umana — `verifiedAt` lo scrive il
 * worker alla generazione (src/worker/index.ts), è la verifica automatica, non una persona che ha
 * riletto. È quindi un'asserzione editoriale dell'editore, vera quanto il processo che c'è dietro, e
 * su una pagina che nomina l'autore in `PostMeta` ricade su quella persona. Per derivarla come il
 * resto della riga servono un `reviewedBy`/`reviewedAt` nel gruppo `provenance` (Posts.ts) scritti
 * alla pubblicazione, e questa stringa condizionata a quelli.
 *
 * Resta la dichiarazione sull'immagine, che sta accanto all'immagine (ArticleView).
 */
export default function Provenance({
  provenance,
  sourceCount,
  className,
}: {
  provenance?: Post['provenance'] | null
  sourceCount: number
  className?: string
}) {
  if (!provenance?.generationType) return null

  const facts = provenance.ledgerFacts ?? 0
  const institutional = provenance.sourcesInstitutional ?? 0
  const verified = provenance.verifiedAt ? new Date(provenance.verifiedAt) : null

  const parts = [
    'Testo scritto con l\'intelligenza artificiale e revisionato dall\'autore',
    facts && sourceCount ? `${facts} ${facts === 1 ? 'dato' : 'dati'} da ${sourceCount} ${sourceCount === 1 ? 'fonte' : 'fonti'}${institutional ? `, di cui ${institutional} ${institutional === 1 ? 'istituzionale' : 'istituzionali'}` : ''}` : null,
    verified ? `verificato il ${verified.toLocaleDateString('it-IT')}` : null,
  ].filter((p): p is string => Boolean(p))

  // Il join separa con un punto, quindi ogni pezzo apre una frase e la maiuscola la mette il join.
  // Senza, in pagina si leggeva «…revisionato dall'autore. verificato il 31/07/2026»: i pezzi erano
  // scritti per stare in coda a una virgola, e il punto e arrivato dopo.
  const line = parts.map((p) => `${p.charAt(0).toUpperCase()}${p.slice(1)}`).join('. ')

  // LA FRASE SULLE FONTI SI DERIVA DAI DATI, e non è una sottigliezza.
  //
  // Era fissa: «Nessun dato di prezzo, orario o accessibilita e stato pubblicato senza fonte
  // ufficiale». Misurata sul post 44, che pubblica 44 euro, 7-13 euro e 9:00-16:00 con
  // `sourcesInstitutional: 0` e 23 fatti su 23 di host non pubblico, era **falsa** — ed era la frase
  // con cui la pagina chiedeva al lettore di fidarsi. Una dichiarazione di affidabilità che non
  // dipende dai dati che descrive è esattamente il genere di affermazione non falsificabile che
  // questo lavoro doveva togliere dalla pagina, non aggiungerci.
  const sourcing = institutional
    ? 'I dati di prezzo, orario e accesso riportano la fonte e la data accanto al valore, e almeno una fonte è istituzionale.'
    : 'I dati di prezzo, orario e accesso riportano la fonte e la data accanto al valore, ma nessuna delle fonti consultate è un ente pubblico: vanno verificati alla fonte ufficiale prima di partire.'

  const access = 'L\'accessibilità non è verificata sul posto.'

  // Un pezzo che non ha passato il gate non deve mostrare la stessa riga di uno che l'ha passato.
  // È lo scenario storico: il post al centro dell'audit l'ha pubblicato una persona a mano, fuori
  // dall'hook, e la pagina non ne portava traccia.
  const blocked = provenance.gatePass === false
  const note = blocked
    ? 'Questa pagina non ha superato i controlli automatici di contenuto ed è stata pubblicata da una persona: le informazioni pratiche vanno verificate alla fonte.'
    : ''

  // LA RIGA SI COMPONE QUI, non a pezzi dentro il JSX.
  //
  // In pagina si leggeva «…una fonte è istituzionale.L'accessibilità non è verificata»: lo spazio
  // c'era nel sorgente, ma era whitespace di bordo di un nodo di testo JSX — quello che il
  // compilatore toglie — e spariva in build. Ogni frase è una voce dell'array e il separatore è uno
  // e uno solo: così lo spazio è un dato, e non dipende da come è mandato a capo il file.
  return (
    <p className={className ?? 'mt-4 max-w-3xl text-xs leading-relaxed text-zinc-500'}>
      {[`${line}.`, sourcing, access, note].filter(Boolean).join(' ')}
    </p>
  )
}
